export default {
  name: 'EXPENSE_MODULE',
  state: {
    expenses: [],
    userExpenseRequests: [], // Store user expense requests
    pendingExpenseRequests: [], // Store pending expense requests that need approval
    expenseTypes: [],
    parentExpenseTypes: [], // Top-level expense types
    childExpenseTypes: {}, // Child expense types by parent ID
    isLoading: false,
    error: null,
    dashboardSummary: null, // Store dashboard summary data
    pendingOtpDeposits: [],   // Expense requests awaiting a kazang-api deposit OTP
    pendingOtpLoading: false,
    categories: [
      'Office Supplies',
      'Travel',
      'Meals',
      'Equipment',
      'Utilities',
      'Maintenance',
      'Software',
      'Other'
    ],
    filters: {
      dateRange: {
        from: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().substr(0, 10),
        to: new Date().toISOString().substr(0, 10)
      },
      processed: false,
      category: null
    }
  },
  getters: {
    allExpenses: state => state.expenses,
    // isLoading: state => state.isLoading,
    error: state => state.error,
    expenseCategories: state => state.categories,
    dateRange: state => state.filters.dateRange,
    // getApiKey: state => state.apiKey,
    userExpenseRequests: state => state.userExpenseRequests,
    pendingExpenseRequests: state => state.pendingExpenseRequests,
    dashboardSummary: state => state.dashboardSummary,

    // Expense Types getters
    allExpenseTypes: state => state.expenseTypes,
    parentExpenseTypes: state => state.parentExpenseTypes,
    childExpenseTypes: state => state.childExpenseTypes,

    // Get child expense types for a specific parent ID
    getChildExpenseTypes: state => parentId => {
      return state.childExpenseTypes[parentId] || [];
    },

    // Get an expense type by ID
    getExpenseTypeById: state => id => {
      return state.expenseTypes.find(type => type.id === id);
    },

    // Get active expense types
    activeExpenseTypes: state => {
      return state.expenseTypes.filter(type => type.isActive);
    },

    filteredExpenses: state => {
      let filtered = [...state.expenses];

      // Filter by category if selected
      if (state.filters.category) {
        filtered = filtered.filter(expense =>
          expense.category === state.filters.category
        );
      }

      return filtered;
    }
  },
  mutations: {
    SET_LOADING(state, isLoading) {
      state.isLoading = isLoading;
    },
    SET_EXPENSES(state, expenses) {
      state.expenses = expenses;
    },
    SET_USER_EXPENSE_REQUESTS(state, requests) {
      state.userExpenseRequests = requests;
    },
    SET_PENDING_EXPENSE_REQUESTS(state, requests) {
      state.pendingExpenseRequests = requests;
    },
    SET_ERROR(state, error) {
      state.error = error;
    },
    CLEAR_ERROR(state) {
      state.error = null;
    },
    SET_DASHBOARD_SUMMARY(state, summary) {
      state.dashboardSummary = summary;
    },
    SET_DATE_RANGE(state, dateRange) {
      state.filters.dateRange = dateRange;
    },
    SET_PROCESSED_FILTER(state, processed) {
      state.filters.processed = processed;
    },
    SET_CATEGORY_FILTER(state, category) {
      state.filters.category = category;
    },

    // Expense Types mutations
    SET_EXPENSE_TYPES(state, expenseTypes) {
      state.expenseTypes = expenseTypes || [];

      // Separate parent and child expense types
      state.parentExpenseTypes = expenseTypes ? expenseTypes.filter(type => !type.parentId) : [];

      // Group child expense types by parent ID
      const childTypes = {};
      if (expenseTypes) {
        expenseTypes.filter(type => type.parentId).forEach(type => {
          if (!childTypes[type.parentId]) {
            childTypes[type.parentId] = [];
          }
          childTypes[type.parentId].push(type);
        });
      }
      state.childExpenseTypes = childTypes;
    },

    ADD_EXPENSE_TYPE(state, expenseType) {
      state.expenseTypes.push(expenseType);

      // Update parent or child collections
      if (!expenseType.parentId) {
        state.parentExpenseTypes.push(expenseType);
      } else {
        if (!state.childExpenseTypes[expenseType.parentId]) {
          state.childExpenseTypes[expenseType.parentId] = [];
        }
        state.childExpenseTypes[expenseType.parentId].push(expenseType);
      }
    },

    UPDATE_EXPENSE_TYPE(state, updatedType) {
      // Update in main collection
      const index = state.expenseTypes.findIndex(type => type.id === updatedType.id);
      if (index !== -1) {
        state.expenseTypes.splice(index, 1, updatedType);
      }

      // Update in parent collection if it's a parent
      if (!updatedType.parentId) {
        const parentIndex = state.parentExpenseTypes.findIndex(type => type.id === updatedType.id);
        if (parentIndex !== -1) {
          state.parentExpenseTypes.splice(parentIndex, 1, updatedType);
        } else {
          // It was a child but now it's a parent
          state.parentExpenseTypes.push(updatedType);

          // Remove from previous parent's children
          Object.keys(state.childExpenseTypes).forEach(parentId => {
            const childIndex = state.childExpenseTypes[parentId].findIndex(type => type.id === updatedType.id);
            if (childIndex !== -1) {
              state.childExpenseTypes[parentId].splice(childIndex, 1);
            }
          });
        }
      } else {
        // It's a child
        // Remove from parent collection if it was a parent
        const parentIndex = state.parentExpenseTypes.findIndex(type => type.id === updatedType.id);
        if (parentIndex !== -1) {
          state.parentExpenseTypes.splice(parentIndex, 1);
        }

        // Update in child collection
        if (!state.childExpenseTypes[updatedType.parentId]) {
          state.childExpenseTypes[updatedType.parentId] = [];
        }

        const childIndex = state.childExpenseTypes[updatedType.parentId].findIndex(type => type.id === updatedType.id);
        if (childIndex !== -1) {
          state.childExpenseTypes[updatedType.parentId].splice(childIndex, 1, updatedType);
        } else {
          state.childExpenseTypes[updatedType.parentId].push(updatedType);
        }
      }
    },

    DELETE_EXPENSE_TYPE(state, id) {
      // Find the expense type to be deleted
      const expenseType = state.expenseTypes.find(type => type.id === id);
      if (!expenseType) return;

      // Remove from main collection
      state.expenseTypes = state.expenseTypes.filter(type => type.id !== id);

      // Remove from parent or child collections
      if (!expenseType.parentId) {
        state.parentExpenseTypes = state.parentExpenseTypes.filter(type => type.id !== id);
      } else if (state.childExpenseTypes[expenseType.parentId]) {
        state.childExpenseTypes[expenseType.parentId] = state.childExpenseTypes[expenseType.parentId].filter(type => type.id !== id);
      }
    },
    SET_PENDING_OTP_DEPOSITS(state, list) {
      state.pendingOtpDeposits = Array.isArray(list) ? list : [];
    },
    SET_PENDING_OTP_LOADING(state, isLoading) {
      state.pendingOtpLoading = !!isLoading;
    }
  },
  actions: {
    async fetchDashboardSummary(context) {
      try {
        context.commit('SET_LOADING', true);
        context.commit('CLEAR_ERROR');

        const apiKey = context.getters.getterApikey;
        const url = `${pettyCashApi}api/reports/dashboard/summary`;

        const response = await axios.get(url, {
          headers: {
            'Content-Type': 'application/json',
            'X-API-KEY': apiKey
          }
        });

        if (response.data) {
          context.commit('SET_DASHBOARD_SUMMARY', response.data);
          return response.data;
        } else {
          throw new Error('Failed to fetch dashboard summary');
        }
      } catch (error) {
        context.commit('SET_ERROR', error.message || 'Failed to fetch dashboard summary');
        throw error;
      } finally {
        context.commit('SET_LOADING', false);
      }
    },
    async approveExpenseRequest(context, { expenseRequestId, id, sessionId }) {
      try {
        context.commit('SET_LOADING', true);
        context.commit('CLEAR_ERROR');

        const apiKey = context.getters.getterApikey;
        const url = `${pettyCashApi}api/expense-requests/${expenseRequestId}/approve?processedBy=${id}`;

        const headers = {
          'Content-Type': 'application/json',
          'X-API-KEY': apiKey
        };
        if (sessionId) {
          headers['X-Kazang-Session-Id'] = sessionId;
        }

        const response = await axios.post(url, {}, { headers });

        if (response.data && response.data.success) {
          // Refresh the pending expense requests
          context.dispatch('fetchPendingExpenseRequests');
          return response.data;
        } else {
          throw new Error(response.data?.message || 'Failed to approve expense request');
        }
      } catch (error) {
        context.commit('SET_ERROR', error.message || 'Failed to approve expense request');
        // Bubble the error so callers can react; the caller (Vue component)
        // is responsible for showing the user-facing alert.
        throw error;
      } finally {
        context.commit('SET_LOADING', false);
      }
    },

    async declineExpenseRequest(context, { expenseRequestId, reason = '', id }) {
      try {
        context.commit('SET_LOADING', true);
        context.commit('CLEAR_ERROR');

        const apiKey = context.getters.getterApikey;
        const url = `${pettyCashApi}api/expense-requests/${expenseRequestId}/decline?processedBy=${id}&declineReason=${reason}`;

        const response = await axios.post(url,{},{
          headers: {
            'X-API-KEY': apiKey
          }
        });

        if (response.data && response.data.success) {
          // Refresh the pending expense requests
          context.dispatch('fetchPendingExpenseRequests');
          return response.data;
        } else {
          throw new Error(response.data?.message || 'Failed to decline expense request');
        }
      } catch (error) {
        context.commit('SET_ERROR', error.message || 'Failed to decline expense request');

        // Show error notification
        Swal.fire({
          title: 'Error',
          text: error.message || 'Failed to decline expense request',
          icon: 'error'
        });

        errorHandler.tomcatError(error);
        throw error;
      } finally {
        context.commit('SET_LOADING', false);
      }
    },

    async fetchPendingExpenseRequests(context, { branchId = null } = {}) {
      try {
        context.commit('SET_LOADING', true);
        context.commit('CLEAR_ERROR');

        const apiKey = context.getters.getterApikey;
        let url = `${pettyCashApi}api/expense-requests/pending`;

        // Add branchId as query parameter if provided
        if (branchId) {
          url += `?branchId=${branchId}`;
        }

        const response = await axios.get(url, {
          headers: {
            'X-API-KEY': apiKey
          }
        });

        if (response.data) {
          context.commit('SET_PENDING_EXPENSE_REQUESTS', response.data.pendingRequests);
          return response.data.pendingRequests;
        } else {
          context.commit('SET_PENDING_EXPENSE_REQUESTS', []);
          return [];
        }
      } catch (error) {
        context.commit('SET_ERROR', error.message || 'Failed to load pending expense requests');

        // Show error notification
        Swal.fire({
          title: 'Error',
          text: 'Failed to load pending expense requests',
          icon: 'error'
        });

        context.commit('SET_PENDING_EXPENSE_REQUESTS', []);
        errorHandler.tomcatError(error);
        throw error;
      } finally {
        context.commit('SET_LOADING', false);
      }
    },

    async fetchUserExpenses(context) {
      try {
        context.commit('SET_LOADING', true);
        context.commit('CLEAR_ERROR');

        // Get the current user from store
        const username = context.rootState.user?.username;

        if (!username) {
          console.error('No user found in store');
          context.commit('SET_EXPENSES', []);
          return;
        }

        const { from, to } = context.state.filters.dateRange;
        const processed = context.state.filters.processed;
        const apiKey = context.getters.getterApikey;

        const url = `${pettyCashApi}statistics/getExpensesByUser?user=${username}&processed=${processed}&from=${from}&to=${to}`;

        const response = await axios.get(url, {
          headers: {
            'X-API-KEY': apiKey
          }
        });

        // Update expenses data
        context.commit('SET_EXPENSES', response.data || []);
        return response.data;
      } catch (error) {
        context.commit('SET_ERROR', error.message || 'Failed to load expenses. Please try again.');

        // Show error notification
        Swal.fire({
          title: 'Error',
          text: 'Failed to load expenses. Please try again.',
          icon: 'error'
        });

        context.commit('SET_EXPENSES', []);
        errorHandler.tomcatError(error);
        throw error;
      } finally {
        context.commit('SET_LOADING', false);
      }
    },

    async fetchUserExpenseRequests(context, userId = '2') {
      try {
        context.commit('SET_LOADING', true);
        context.commit('CLEAR_ERROR');

        const apiKey = context.getters.getterApikey;
        const url = `${pettyCashApi}api/expense-requests/user/${userId}`;

        const response = await axios.get(url, {
          headers: {
            'X-API-KEY': apiKey
          }
        });

        if (response.data && response.data.userRequests) {
          context.commit('SET_USER_EXPENSE_REQUESTS', response.data.userRequests);
          return response.data.userRequests;
        } else {
          context.commit('SET_USER_EXPENSE_REQUESTS', []);
          return [];
        }
      } catch (error) {
        context.commit('SET_ERROR', error.message || 'Failed to load user expense requests');

        // Show error notification
        Swal.fire({
          title: 'Error',
          text: 'Failed to load user expense requests',
          icon: 'error'
        });

        context.commit('SET_USER_EXPENSE_REQUESTS', []);
        errorHandler.tomcatError(error);
        throw error;
      } finally {
        context.commit('SET_LOADING', false);
      }
    },

    async createExpenseRequest(context, payload) {
      try {
        context.commit('SET_LOADING', true);
        context.commit('CLEAR_ERROR');

        const apiKey = context.getters.getterApikey;
        const url = `${pettyCashApi}api/expense-requests/create`;

        const response = await axios.post(url, payload, {
          headers: {
            'Content-Type': 'application/json',
            'X-API-KEY': apiKey
          }
        });

        if (response.data && response.data.success) {
          // Check if user has permission to approve expense requests
          const user = context.getters.getUserRole;
          const userId = context.getters.CurrentUserInfo;
          const canApproveExpenseRequest = user.can_approve_expense_request;

          let successMessage = 'Expense request created successfully';

          // If user has approval permission and auto-approve is enabled, auto-approve the request
          if (canApproveExpenseRequest && payload.autoApprove && response.data.expenseRequest && response.data.expenseRequest.id) {
            try {
              // Get the current user ID for the processedBy parameter
              // const userId = user.id || user.userId;

              // Call the approveExpenseRequest action to auto-approve the request
              await context.dispatch('approveExpenseRequest', {
                expenseRequestId: response.data.expenseRequest.id,
                id: userId.id
              });

              // Update success message to indicate auto-approval
              successMessage = 'Expense request created and automatically approved';
            } catch (approvalError) {
              console.error('Auto-approval failed:', approvalError);
              // Don't throw error here, as the request was still created successfully
            }
          }

          // Handle GRV payment if this is a GRV Payment expense
          if (payload.expenseType === 'GRV Payment' && payload.expenseMethod && payload.expenseUrl && payload.expensepayload) {
            try {
              console.log('Processing GRV payment...');
              console.log('GRV payment URL:', payload.expenseUrl);
              console.log('GRV payment payload:', payload.expensepayload);

              // Parse the stringified payload if needed
              let paymentData;
              if (typeof payload.expensepayload === 'string') {
                paymentData = JSON.parse(payload.expensepayload);
              } else {
                paymentData = payload.expensepayload;
              }

              // Make the GRV payment API call
              const paymentResponse = await axios.post(payload.expenseUrl, paymentData, {
                headers: {
                  'Content-Type': 'application/json'
                }
              });

              console.log('GRV payment response:', paymentResponse.data);

              // Update success message to include GRV payment
              successMessage = 'GRV Payment submitted successfully!';

            } catch (paymentError) {
              console.error('GRV payment failed:', paymentError);

              // Check if it's a 400 error (validation issue)
              if (paymentError.response && paymentError.response.status === 400) {
                throw new Error(`GRV Payment failed: ${paymentError.response.data?.message || 'Invalid GRV data'}`);
              }

              // Check for other HTTP errors
              if (paymentError.response) {
                throw new Error(`GRV Payment failed: HTTP ${paymentError.response.status} - ${paymentError.response.statusText}`);
              }

              // Network or other errors
              throw new Error(`GRV Payment failed: ${paymentError.message}`);
            }
          }

          // Show success notification
          Swal.fire({
            title: 'Success',
            text: successMessage,
            icon: 'success'
          });

          return response.data.data;
        } else {
          throw new Error(response.data?.message || 'Failed to create expense request');
        }
      } catch (error) {
        context.commit('SET_ERROR', error.message || 'Failed to create expense request');

        // Show error notification
        Swal.fire({
          title: 'Error',
          text: error.message || 'Failed to create expense request',
          icon: 'error'
        });

        throw error;
      } finally {
        context.commit('SET_LOADING', false);
      }
    },

    async updateExpenseRequestImages(context, { requestId, images }) {
      try {
        context.commit('SET_LOADING', true);
        context.commit('CLEAR_ERROR');

        const apiKey = context.getters.getterApikey;
        const url = `${pettyCashApi}api/expense-requests/${requestId}/images`;

        const response = await axios.put(url, { images }, {
          headers: {
            'Content-Type': 'application/json',
            'X-API-KEY': apiKey
          }
        });

        if (response.data && response.data.success) {
          return response.data.data;
        } else {
          throw new Error(response.data?.message || 'Failed to update expense request images');
        }
      } catch (error) {
        context.commit('SET_ERROR', error.message || 'Failed to update expense request images');
        errorHandler.tomcatError(error);
        throw error;
      } finally {
        context.commit('SET_LOADING', false);
      }
    },

    updateDateRange(context, dateRange) {
      context.commit('SET_DATE_RANGE', dateRange);
      context.dispatch('fetchUserExpenses');
    },

    updateProcessedFilter(context, processed) {
      context.commit('SET_PROCESSED_FILTER', processed);
      context.dispatch('fetchUserExpenses');
    },

    updateCategoryFilter(context, category) {
      context.commit('SET_CATEGORY_FILTER', category);
    },

    // Expense Types actions
    async fetchAllExpenseTypes(context, { activeOnly = false, parentsOnly = false } = {}) {
      try {
        context.commit('SET_LOADING', true);
        context.commit('CLEAR_ERROR');

        const url = `${pettyCashApi}api/expense-types`;
        const headers = { "X-API-KEY": context.getters.getterApikey};

        const response = await axios.get(url, { headers });

        if (response.data && response.data.success ) {
          context.commit('SET_EXPENSE_TYPES', response.data.expenseTypes);
          return response.data.expenseTypes;
        } else {
          throw new Error(response.data?.message || 'Failed to fetch expense types');
        }
      } catch (error) {
        context.commit('SET_ERROR', error.message || 'Failed to fetch expense types');
        errorHandler.tomcatError(error);
        throw error;
      } finally {
        context.commit('SET_LOADING', false);
      }
    },

    async fetchExpenseTypeById(context, id) {
      try {
        context.commit('SET_LOADING', true);
        context.commit('CLEAR_ERROR');

        const apiKey = context.getters.getterApikey;
        const url = `${pettyCashApi}api/expense-types/${id}`;

        const response = await axios.get(url, {
          headers: {
            'X-API-KEY': apiKey
          }
        });

        if (response.data && response.data.success) {
          return response.data.data;
        } else {
          throw new Error(response.data?.message || 'Failed to fetch expense type');
        }
      } catch (error) {
        context.commit('SET_ERROR', error.message || 'Failed to fetch expense type');
        errorHandler.tomcatError(error);
        throw error;
      } finally {
        context.commit('SET_LOADING', false);
      }
    },

    async fetchExpenseTypesByParentId(context, parentId) {
      try {
        context.commit('SET_LOADING', true);
        context.commit('CLEAR_ERROR');

        const apiKey = context.getters.getterApikey;
        const url = `${pettyCashApi}api/expense-types/parent/${parentId}`;

        const response = await axios.get(url, {
          headers: {
            'X-API-KEY': apiKey
          }
        });

        if (response.data && response.data.success) {
          return response.data.data;
        } else {
          throw new Error(response.data?.message || 'Failed to fetch child expense types');
        }
      } catch (error) {
        context.commit('SET_ERROR', error.message || 'Failed to fetch child expense types');
        errorHandler.tomcatError(error);
        throw error;
      } finally {
        context.commit('SET_LOADING', false);
      }
    },

    async createExpenseType(context, expenseTypeData) {
      try {
        context.commit('SET_LOADING', true);
        context.commit('CLEAR_ERROR');

        const apiKey = context.getters.getterApikey;
        const url = `${pettyCashApi}api/expense-types`;

        const response = await axios.post(url, expenseTypeData, {
          headers: {
            'Content-Type': 'application/json',
            'X-API-KEY': apiKey
          }
        });

        if (response.data && response.data.success) {
          // Add the new expense type to the store
          const expenseType = response.data.expenseType || response.data.data;
          if (expenseType) {
            context.commit('ADD_EXPENSE_TYPE', expenseType);
            return expenseType;
          } else {
            throw new Error('Expense type data not found in response');
          }
        } else {
          throw new Error(response.data?.message || 'Failed to create expense type');
        }
      } catch (error) {
        context.commit('SET_ERROR', error.message || 'Failed to create expense type');
        errorHandler.tomcatError(error);
        throw error;
      } finally {
        context.commit('SET_LOADING', false);
      }
    },

    async updateExpenseType(context, { id, expenseTypeData }) {
      try {
        context.commit('SET_LOADING', true);
        context.commit('CLEAR_ERROR');

        const apiKey = context.getters.getterApikey;
        const url = `${pettyCashApi}api/expense-types/${id}`;

        const response = await axios.put(url, expenseTypeData, {
          headers: {
            'Content-Type': 'application/json',
            'X-API-KEY': apiKey
          }
        });

        if (response.data && response.data.success) {
          // Update the expense type in the store
          context.commit('UPDATE_EXPENSE_TYPE', response.data.data);
          return response.data.data;
        } else {
          throw new Error(response.data?.message || 'Failed to update expense type');
        }
      } catch (error) {
        context.commit('SET_ERROR', error.message || 'Failed to update expense type');
        errorHandler.tomcatError(error);
        throw error;
      } finally {
        context.commit('SET_LOADING', false);
      }
    },

    async deleteExpenseType(context, id) {
      try {
        context.commit('SET_LOADING', true);
        context.commit('CLEAR_ERROR');

        const apiKey = context.getters.getterApikey;
        const url = `${pettyCashApi}api/expense-types/${id}`;

        const response = await axios.delete(url, {
          headers: {
            'X-API-KEY': apiKey
          }
        });

        if (response.data && response.data.success) {
          // Remove the expense type from the store
          context.commit('DELETE_EXPENSE_TYPE', id);
          return true;
        } else {
          throw new Error(response.data?.message || 'Failed to delete expense type');
        }
      } catch (error) {
        context.commit('SET_ERROR', error.message || 'Failed to delete expense type');
        errorHandler.tomcatError(error);
        throw error;
      } finally {
        context.commit('SET_LOADING', false);
      }
    },

    async fetchExpenseImage(context, expenseRequestId) {
      try {
        context.commit('SET_LOADING', true);
        context.commit('CLEAR_ERROR');

        const apiKey = context.getters.getterApikey;
        const url = `${pettyCashApi}api/expense-requests/${expenseRequestId}/image`;

        const response = await axios.get(url, {
          headers: {
            'X-API-KEY': apiKey
          },
          responseType: 'blob'
        });

        if (response.data) {
          // Convert blob to base64 for display
          return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(response.data);
          });
        } else {
          throw new Error('Failed to fetch expense image');
        }
      } catch (error) {
        context.commit('SET_ERROR', error.message || 'Failed to fetch expense image');
        errorHandler.tomcatError(error);
        throw error;
      } finally {
        context.commit('SET_LOADING', false);
      }
    },

    async fetchApprovalStatsToday(context, userId) {
      try {
        context.commit('SET_LOADING', true);
        context.commit('CLEAR_ERROR');

        const apiKey = context.getters.getterApikey;
        const url = `${pettyCashApi}users/${userId}/approval-stats/today`;

        const response = await axios.get(url, {
          headers: {
            'X-API-KEY': apiKey
          }
        });

        if (response.data) {
          return response.data;
        } else {
          // Return default stats if API doesn't exist yet
          return {
            approvedToday: 0,
            declinedToday: 0,
            pendingApproval: 0
          };
        }
      } catch (error) {
        console.error('Error fetching approval stats:', error);
        // Return default stats on error instead of throwing
        return {
          approvedToday: 0,
          declinedToday: 0,
          pendingApproval: 0
        };
      } finally {
        context.commit('SET_LOADING', false);
      }
    },

    /**
     * Fetch the list of PENDING_OTP expense requests (those awaiting a
     * kazang-api deposit OTP). The backend enriches each row with the
     * in-memory session state so the UI can distinguish "Active OTP" from
     * "Session lost".
     */
    async fetchPendingOtpDeposits(context, { branchId = null } = {}) {
      try {
        context.commit('SET_PENDING_OTP_LOADING', true);
        const apiKey = context.getters.getterApikey;
        const params = new URLSearchParams();
        if (branchId) params.append('branchId', branchId);
        const url = `${pettyCashApi}api/expense-requests/pending-otp?${params.toString()}`;
        const response = await axios.get(url, {
          headers: { 'X-API-KEY': apiKey }
        });
        context.commit('SET_PENDING_OTP_DEPOSITS', response.data || []);
        return response.data || [];
      } catch (error) {
        context.commit('SET_PENDING_OTP_DEPOSITS', []);
        errorHandler.tomcatError(error);
        throw error;
      } finally {
        context.commit('SET_PENDING_OTP_LOADING', false);
      }
    },

    /**
     * Submit the kazang-api deposit Phase 2 OTP. On success the request flips
     * to APPROVED server-side; we re-fetch the pending list and the standard
     * pending requests list. Throws so the caller can react to 401 (session
     * expired / wrong OTP) by offering a Restart.
     */
    async completeExpenseDeposit(context, { id, otp }) {
      try {
        const apiKey = context.getters.getterApikey;
        const url = `${pettyCashApi}api/expense-requests/${id}/complete-deposit`;
        const response = await axios.post(url,
          { otp },
          { headers: { 'X-API-KEY': apiKey, 'Content-Type': 'application/json' } }
        );
        context.dispatch('fetchPendingOtpDeposits');
        context.dispatch('fetchPendingExpenseRequests');
        return response.data;
      } catch (error) {
        throw error;
      }
    },

    /**
     * Re-issue Phase 1 for a PENDING_OTP expense request — used when the
     * in-memory session is gone (backend restart) or Phase 2 failed with a
     * 401. Returns the new { transactionId, phoneHint, expiresAt }.
     */
    async restartExpenseDeposit(context, { id }) {
      try {
        const apiKey = context.getters.getterApikey;
        const url = `${pettyCashApi}api/expense-requests/${id}/restart-deposit`;
        const response = await axios.post(url,
          {},
          { headers: { 'X-API-KEY': apiKey, 'Content-Type': 'application/json' } }
        );
        context.dispatch('fetchPendingOtpDeposits');
        return response.data;
      } catch (error) {
        Swal.fire({
          title: 'Restart failed',
          text: (error && error.response && error.response.data && error.response.data.message)
            || 'Could not restart deposit',
          icon: 'error'
        });
        errorHandler.tomcatError(error);
        throw error;
      }
    }
  }
};
